/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import AudioControls from './AudioControls';
import VolumeControl from './VolumeControl';
import ShareModal from './shareModal';
import QueuePopup from './QueuePopup';
import { usePlay } from '../context/Play';
import { trigger, on, off } from '../utils/events';
import { useApi } from '../context/ApiProvider';
import { timeFormat } from '../utils/timeFormat';
import { getImg } from '../utils/getImg';
import {
  Box,
  Slider,
  Stack,
  Typography,
  Link,
  Avatar,
  Tooltip,
  styled,
  SnackbarContent,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import defaultArt from '../assets/images/dj_kip_art.png';
import { getMediaSessionArtistString } from '../utils/sponsorUtils';
import { isAuthDebugEnabled } from '../utils/authDebug';

const FixedContainer = styled('div')`
  width: 100%;
  position: fixed;
  bottom: 0px;
  z-index: 1001;
`;

const AudioPlayerStyled = styled('div')(({ theme }) => ({
  backgroundColor: '#333337',
  height: '70px',
  padding: '10px 30px',

  [theme.breakpoints.down('sm')]: {
    height: '145px',
    padding: '10px',
  },

  '.content': {
    display: 'flex',
    justifyContent: 'space-between',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}));

const StyledSnackbarContent = styled(SnackbarContent)({
  backgroundColor: '#DEFF7B !important',
  color: '#000 !important',
  fontFamily: 'Pixel, Helvetica, Arial, sans-serif',
  fontWeight: 700,
  '& .MuiSnackbarContent-message': {
    fontFamily: 'Pixel, Helvetica, Arial, sans-serif',
    fontWeight: 700,
  },
});

const TrackInfo = styled('div')`
  position: relative;
  width: 33%;
  display: flex;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Controls = styled('div')`
  width: 33%;
  text-align: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AudioPlayer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const api = useApi();
  const intervalRef = useRef();
  const trackPlaysIntervalRef = useRef();
  const authRetryCount = useRef(0);
  const failedRequests = useRef(new Set());
  const debugMode = isAuthDebugEnabled();

  // Like state the player learned after the set loaded, by track_meta_id:
  // { show_likes, user_likes }. The set's own track_meta is the fallback.
  const [trackLikeUpdates, setTrackLikeUpdates] = useState({});
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(false);
  const [trackProgress, setTrackProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    isPlaying,
    setIsPlaying,
    playingSet,
    audioRef,
    playPause,
    onSetEnded,
    trackVolume,
    setTrackVolume,
    audioDuration,
    isLoadingMetadata,
    setPlayingProgress,
    setPlayingTrackIndexInSet,
  } = usePlay();

  const {
    id,
    set_name,
    artist_meta,
    art_url_sm,
    art_url_xl,
    set_sponsors,
    set_tracks,
    user_fav,
  } = playingSet;

  const debug = (message, data = null) => {
    if (debugMode) {
      console.log(`🔧 AudioPlayer: ${message}`, data);
    }
  };

  // Listen for token refresh events (stabilized)
  useEffect(() => {
    const handleTokenRefresh = () => {
      debug('Token refreshed, retrying failed requests', {
        failedCount: failedRequests.current.size,
        retryCount: authRetryCount.current,
      });

      // Reset retry count
      authRetryCount.current = 0;

      // Retry failed track plays
      if (trackProgress && id && failedRequests.current.has('trackPlays')) {
        debug('Retrying failed track plays');
        trackPlays(trackProgress);
        failedRequests.current.delete('trackPlays');
      }

      // Retry failed likes
      if (failedRequests.current.has('like')) {
        debug('Retrying failed like');
        handleLike();
        failedRequests.current.delete('like');
      }
    };

    const handleAuthError = (data) => {
      debug('Auth error in audio player', data);

      if (!document.hidden) {
        enqueueSnackbar('Please hold, KIP is loading . . .', {
          variant: 'info',
          autoHideDuration: 2000,
          content: (key, message) => (
            <StyledSnackbarContent id={key} message={message} />
          ),
        });
      }
    };

    on('tokenRefreshed', handleTokenRefresh);
    on('authError', handleAuthError);

    return () => {
      off('tokenRefreshed', handleTokenRefresh);
      off('authError', handleAuthError);
    };
  }, [id]);

  // A newly loaded set carries fresh like state for its tracks.
  useEffect(() => {
    setTrackLikeUpdates({});
  }, [id]);

  function applyTrackLike(trackMetaId, update) {
    if (!trackMetaId) return;
    setTrackLikeUpdates((prev) => ({
      ...prev,
      [trackMetaId]: { ...prev[trackMetaId], ...update },
    }));
  }

  // A like toggled in a tracklist row updates the player's heart too.
  useEffect(() => {
    const handleTrackLikesChanged = (event) => {
      const { track_meta_id, show_likes, user_likes } = event.detail || {};
      applyTrackLike(track_meta_id, { show_likes, user_likes });
    };
    on('trackLikesChanged', handleTrackLikesChanged);
    return () => off('trackLikesChanged', handleTrackLikesChanged);
  }, []);

  function startTimer() {
    clearInterval(intervalRef.current);
    clearInterval(trackPlaysIntervalRef.current);

    if (audioRef && !audioRef.paused) {
      trackPlays(audioRef.currentTime);
    }

    intervalRef.current = setInterval(() => {
      if (audioRef?.ended) {
        setIsPlaying(false);
        setTrackProgress(0);
        setPlayingProgress(0);
        clearInterval(intervalRef.current);
        clearInterval(trackPlaysIntervalRef.current);
        onSetEnded(id);
      } else if (!audioRef || audioRef.paused) {
        return;
      } else {
        const currentTime = audioRef.currentTime;
        setTrackProgress(currentTime);
        setPlayingProgress(currentTime);
      }
    }, [1000]);

    // call trackPlays every 30 seconds
    trackPlaysIntervalRef.current = setInterval(() => {
      if (!audioRef || audioRef.paused) return;

      if (audioRef.ended) {
        clearInterval(trackPlaysIntervalRef.current);
      } else {
        trackPlays(audioRef.currentTime);
      }
    }, [30000]);
  }

  useEffect(() => {
    if (!set_tracks || !set_tracks.length) {
      setPlayingTrackIndexInSet(null);
      return;
    }
    const currentTrackPos = getPlayingTrackPosition();
    setPlayingTrackIndexInSet(currentTrackPos >= 0 ? currentTrackPos : null);
    if (currentTrackPos >= 0) setCurrentTrack(set_tracks[currentTrackPos]);
  }, [trackProgress, set_tracks, setPlayingTrackIndexInSet]);

  const currentTitle = useMemo(() => {
    if (!currentTrack) return '';
    const artistNames = currentTrack?.track_meta?.artists
      ?.filter((artist) => {
        return (
          artist?.role?.toLowerCase() === 'id' ||
          artist?.role?.toLowerCase() === 'producer'
        );
      })
      ?.map((artist) => artist?.artist_meta?.name)
      ?.join(' & ');
    let title = `${artistNames} - ${currentTrack?.track_meta?.title}`;
    if (
      currentTrack?.track_meta?.mix_type &&
      currentTrack?.track_meta?.mix_meta?.name
    ) {
      if (currentTrack.track_meta.mix_type === 'remix') {
        title += ` (${currentTrack.track_meta.mix_meta.name} Remix)`;
      } else if (currentTrack.track_meta.mix_type === 'edit') {
        title += ` (${currentTrack.track_meta.mix_meta.name} Edit)`;
      }
    }
    return title;
  }, [currentTrack]);

  useEffect(() => {
    setMediaSessionInfo(currentTitle);
  }, [currentTitle]);

  // The heart follows the playing track: outline until this user has liked
  // it, then filled with their like count, as in the set's tracklist.
  const currentTrackLike = useMemo(() => {
    const trackMetaId = currentTrack?.track_meta_id;
    if (!trackMetaId) return { liked: false, count: 0 };
    const update = trackLikeUpdates[trackMetaId] || {};
    const meta = currentTrack.track_meta || {};
    return {
      liked: Boolean(update.show_likes ?? meta.show_likes),
      count: update.user_likes ?? meta.user_likes ?? 0,
    };
  }, [currentTrack, trackLikeUpdates]);

  useEffect(() => {
    // Only reset to 0 if this is a completely new set, not when resuming
    if (audioRef && audioRef.currentTime > 0 && !user_fav?.current_timestamp) {
      audioRef.currentTime = 0;
    }
  }, [audioRef]);

  useEffect(() => {
    if (!audioRef) return;

    const startSetAt =
      user_fav?.current_timestamp && user_fav.current_timestamp > 0
        ? user_fav.current_timestamp
        : 0;

    // Use actual currentTime if it's already set, otherwise use startSetAt
    const currentTime = audioRef.currentTime || startSetAt;
    setTrackProgress(currentTime);
    setPlayingProgress(currentTime);

    if (startSetAt > 0 && audioRef.currentTime !== startSetAt) {
      audioRef.currentTime = startSetAt;
      setTrackProgress(startSetAt);
      setPlayingProgress(startSetAt);
    }

    startTimer();
  }, [audioRef]);

  useEffect(() => {
    if (audioRef) {
      audioRef.volume = trackVolume;
    }
  }, [trackVolume]);

  // No local metadata listeners; consume stable duration from context

  function setMediaSessionInfo(trackTitle) {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: trackTitle,
        artist: getMediaSessionArtistString(
          artist_meta?.name,
          set_name,
          set_sponsors
        ),
        artwork: [
          {
            src: getImg(art_url_sm) || getImg(art_url_xl) || defaultArt,
            sizes: '96x96',
            type: 'image/png',
          },
          {
            src: getImg(art_url_sm) || getImg(art_url_xl) || defaultArt,
            sizes: '128x128',
            type: 'image/png',
          },
          {
            src: getImg(art_url_sm) || getImg(art_url_xl) || defaultArt,
            sizes: '192x192',
            type: 'image/png',
          },
          {
            src: getImg(art_url_xl) || getImg(art_url_sm) || defaultArt,
            sizes: '256x256',
            type: 'image/png',
          },
          {
            src: getImg(art_url_xl) || getImg(art_url_sm) || defaultArt,
            sizes: '384x384',
            type: 'image/png',
          },
          {
            src: getImg(art_url_xl) || getImg(art_url_sm) || defaultArt,
            sizes: '512x512',
            type: 'image/png',
          },
        ],
      });

      navigator.mediaSession.setActionHandler('play', () => {
        playPause();
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        playPause();
      });
    }
  }

  function getPlayingTrackPosition() {
    return set_tracks.findIndex(
      (track) =>
        trackProgress >= track.start_time && trackProgress < track.end_time
    );
  }

  function toPrevTrack() {
    const playingTrack = getPlayingTrackPosition();
    const prevTrack = set_tracks[playingTrack - 1];
    if (!prevTrack) return;
    const startTime = Number(prevTrack.start_time);
    onScrub(startTime);
    onScrubEnd();
  }

  function toNextTrack() {
    const playingTrack = getPlayingTrackPosition();
    const nextTrack = set_tracks[playingTrack + 1];
    if (!nextTrack) return;
    const startTime = Number(nextTrack.start_time);
    onScrub(startTime);
    onScrubEnd();
  }

  function onScrub(value) {
    clearInterval(intervalRef.current);
    audioRef.currentTime = value;
    setTrackProgress(value);
    setPlayingProgress(value);
  }

  function onScrubEnd() {
    if (!isPlaying) {
      setIsPlaying(true);
      audioRef.play();
    }
    // Update progress immediately when scrubbing ends
    if (audioRef) {
      setPlayingProgress(audioRef.currentTime);
    }
    startTimer();
  }

  async function handleLike() {
    if (trackProgress && id) {
      // The server credits the like to the track at this timestamp, so keep
      // that track even if the next one starts before the response.
      const likedTrack = currentTrack;
      setLoading(true);
      const data = {
        set_meta_id: id,
        set_timestamp: trackProgress,
      };

      try {
        debug('Sending like request');
        const response = await api.post(`/set-likes`, data);

        if (response.ok) {
          if (likedTrack?.track_meta_id) {
            const meta = likedTrack.track_meta || {};
            const known =
              trackLikeUpdates[likedTrack.track_meta_id]?.user_likes ??
              meta.user_likes ??
              0;
            applyTrackLike(likedTrack.track_meta_id, {
              show_likes: true,
              user_likes: known + 1,
            });
          }
          showLikeNotification(likedTrack);
          callFavorites(likedTrack);
          failedRequests.current.delete('like');
        } else if (response.status === 401) {
          debug('Like request failed with 401');
          authRetryCount.current++;
          failedRequests.current.add('like');

          // Don't show error to user during background playback
          if (!document.hidden) {
            enqueueSnackbar('Authentication expired, refreshing...', {
              variant: 'info',
              autoHideDuration: 2000,
            });
          }
        } else {
          debug('Like request failed', response);
          if (!document.hidden) {
            enqueueSnackbar('Failed to like track', { variant: 'error' });
          }
        }
      } catch (error) {
        debug('Like request error', error);
        failedRequests.current.add('like');

        if (!document.hidden) {
          enqueueSnackbar('Failed to like track', { variant: 'error' });
        }
      } finally {
        setLoading(false);
      }
    }
  }

  async function callFavorites(likedTrack) {
    try {
      await api.put(`/favs/set/${id}/likes`);
      const trackId = likedTrack?.track_meta_id;
      if (!trackId) return;
      const response = await api.put(`/favs/track/${trackId}/likes`);
      if (response.ok) {
        // The server's count wins over the one the heart showed straight away.
        const update = {
          track_meta_id: trackId,
          show_likes: response.body.show_likes,
          user_likes: response.body.total_user_likes,
        };
        applyTrackLike(trackId, update);
        trigger('trackLikesChanged', update);
      }
      trigger('updateMetaLikes');
    } catch (error) {
      debug('Favorites call failed', error);
    }
  }

  function showLikeNotification(likedTrack) {
    const title = likedTrack?.track_meta?.title;
    enqueueSnackbar(
      title ? `${title} has been saved to your likes!` : 'Saved to your likes!'
    );
  }

  async function trackPlays(trackProgress) {
    if (trackProgress && id) {
      const data = {
        set_meta_id: id,
        current_timestamp: trackProgress,
      };

      try {
        debug('Sending track plays request');
        const response = await api.put(`/plays`, data);

        if (response.ok) {
          failedRequests.current.delete('trackPlays');
        } else if (response.status === 401) {
          debug('Track plays request failed with 401');
          authRetryCount.current++;
          failedRequests.current.add('trackPlays');

          // Silent failure in background
          if (!document.hidden) {
            debug('Track plays failed in foreground');
          }
        } else {
          debug('Track plays request failed', response);
        }
      } catch (error) {
        debug('Track plays request error', error);
        failedRequests.current.add('trackPlays');
      }
    }
  }

  let timeDuration = 0;
  let timeProgress = 0;
  let duration = audioDuration || 0;
  if (duration) {
    timeDuration = timeFormat(duration);
  }
  // Always format progress, even if duration isn't loaded yet
  timeProgress = timeFormat(trackProgress);

  // Show loading state or starting timestamp
  // Show time display if we have progress, duration, or are loading metadata
  const showTimeDisplay =
    trackProgress > 0 || duration > 0 || isLoadingMetadata;
  const timeDisplayText = isLoadingMetadata
    ? duration > 0
      ? `${timeProgress} / ${timeDuration}`
      : `${timeProgress} / Loading...`
    : duration > 0
    ? `${timeProgress} / ${timeDuration}`
    : trackProgress > 0
    ? `${timeProgress} / Loading...`
    : '';

  return (
    <>
      <FixedContainer>
        <Slider
          aria-label="Volume"
          value={trackProgress ? trackProgress : 0}
          onChange={(e) => onScrub(e.target.value)}
          onChangeCommitted={() => onScrubEnd()}
          step={1}
          min={0}
          max={duration ? duration : 0}
          sx={{
            padding: '0 !important',
            mb: '-5px',
          }}
        />

        <AudioPlayerStyled>
          <div className="content">
            {playingSet && (
              <TrackInfo>
                <Avatar
                  src={getImg(art_url_xl) || getImg(art_url_sm) || defaultArt}
                  alt={`track artwork for ${set_name} by ${artist_meta?.name}`}
                />
                <Stack
                  direction="column"
                  marginLeft={1}
                  width={{ xs: '75%', md: '100%' }}
                >
                  <Link
                    href=""
                    to={`/tracks/${currentTrack?.track_meta_id}`}
                    underline="hover"
                  >
                    {currentTrack && currentTitle && (
                      <Tooltip title={currentTrack?.track_meta?.title}>
                        <Typography
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {currentTitle}
                        </Typography>
                      </Tooltip>
                    )}
                  </Link>
                  <Link
                    href=""
                    to={`/sets/${playingSet?.id}`}
                    underline="hover"
                  >
                    <Typography
                      variant="body2"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {playingSet?.artist_meta?.name} - {playingSet?.set_name}
                    </Typography>
                  </Link>
                </Stack>
                <Box
                  display={{ xs: 'flex', sm: 'none', md: 'none' }}
                  sx={{ width: '35%', justifyContent: 'flex-end' }}
                >
                  <QueuePopup />
                </Box>
              </TrackInfo>
            )}
            <Controls>
              <AudioControls
                isPlaying={isPlaying}
                onPrevClick={toPrevTrack}
                onNextClick={toNextTrack}
                onPlayPauseClick={playPause}
                onLikeClick={handleLike}
                onShareClick={() => setShareModalOpen(true)}
                loading={loading}
                liked={currentTrackLike.liked}
                likes={currentTrackLike.count}
              />
            </Controls>
            <Stack
              spacing={{ xs: 0, md: 1 }}
              direction={{ xs: 'column', sm: 'row' }}
              width={{ xs: '100%', md: '33%' }}
              justifyContent={{ xs: 'center', sm: 'end' }}
              alignItems={{ xs: 'center', md: 'center' }}
            >
              <Box display={{ md: 'block' }}>
                {showTimeDisplay && (
                  <Typography
                    variant="body2"
                    color={isLoadingMetadata ? 'text.secondary' : 'white'}
                    sx={{
                      fontStyle: isLoadingMetadata ? 'italic' : 'normal',
                      minWidth: '100px',
                    }}
                  >
                    {timeDisplayText}
                  </Typography>
                )}
              </Box>
              <Box width={150} display={{ xs: 'none', lg: 'block' }}>
                <VolumeControl
                  setTrackVolume={setTrackVolume}
                  trackVolume={trackVolume}
                />
              </Box>
              <Box display={{ xs: 'none', sm: 'block', md: 'block' }}>
                <QueuePopup />
              </Box>
            </Stack>
          </div>
        </AudioPlayerStyled>
      </FixedContainer>

      <ShareModal
        open={shareModalOpen}
        onCloseModal={() => setShareModalOpen(false)}
        setId={id}
        setMessage={`Check out this set by ${artist_meta?.name} on KIP: `}
        trackId={currentTrack?.track_meta_id}
        trackMessage={`Check out ${currentTrack?.track_meta?.title} by ${currentTrack?.track_meta?.artist} on KIP: `}
      />
    </>
  );
};

export default AudioPlayer;
